/** Shared requester-only invalidation and bounded freshness recovery. */
import { trace } from "@opentelemetry/api";
export interface JobReference {
  owner: "database_service" | "account_orchestrator";
  kind: string;
  id: string;
  attempt: string;
}

export interface JobSignal {
  schema_version: 1;
  job: JobReference;
  client_id: string;
  version: number;
  resources: { kind: string; id: string }[];
}

export const jobKey = (job: JobReference): string =>
  JSON.stringify([job.owner, job.kind, job.id, job.attempt]);

export interface JobReadResult {
  job?: JobReference;
  version: number;
  unfinished: boolean;
  value?: unknown;
  requester?: boolean;
  accessDenied?: boolean;
  /** Publish fetched data only after the coordinator accepts its version. */
  apply?: () => void;
}

export interface JobObserver {
  /** Read through the authorized owner API; defer cache writes to result.apply. */
  read: (signal: AbortSignal, minimumVersion: number) => Promise<JobReadResult>;
  delayed: (value: boolean) => void;
  requester: boolean;
  received?: (result: JobReadResult) => void;
  failed?: (error: unknown) => void;
}

interface Entry {
  observers: Set<JobObserver>;
  version: number;
  unfinished: boolean;
  denied: boolean;
  checks: number;
  staleRetries: number;
  timer?: ReturnType<typeof setTimeout>;
  request?: AbortController;
  again: boolean;
  result?: JobReadResult;
}

const FRESHNESS_MS = 60_000;
const MAX_CHECKS = 5;

export class JobRefreshCoordinator {
  private entries = new Map<string, Entry>();
  private versions = new Map<string, number>();
  private signalVersions = new Map<string, number>();
  private visible = true;

  observe(job: JobReference, observer: JobObserver): () => void {
    const key = jobKey(job);
    let entry = this.entries.get(key);
    const first = !entry;
    if (!entry) {
      entry = {
        observers: new Set(), version: this.versions.get(key) ?? 0,
        unfinished: true, denied: false, checks: 0, staleRetries: 0, again: false,
      };
      this.entries.set(key, entry);
    }
    entry.observers.add(observer);
    if (entry.result) observer.received?.(entry.result);
    observer.delayed(entry.checks >= MAX_CHECKS);
    if (first && this.visible) void this.refresh(key, entry);
    return () => {
      entry.observers.delete(observer);
      if (!entry.observers.size) {
        this.cancel(entry);
        this.entries.delete(key);
      }
    };
  }

  changed(signal: JobSignal): boolean {
    const key = jobKey(signal.job);
    const prior = this.signalVersions.get(key) ?? 0;
    if (signal.version <= prior) return false;
    this.signalVersions.set(key, signal.version);
    this.versions.set(key, Math.max(this.versions.get(key) ?? 0, signal.version));
    // A retry may reuse the domain ID with a new attempt. Ask mounted older
    // attempts for the authoritative reference; never trust event arrival order.
    for (const [otherKey, otherEntry] of this.entries) {
      const [owner, kind, id] = JSON.parse(otherKey) as string[];
      if (otherKey !== key && owner === signal.job.owner && kind === signal.job.kind && id === signal.job.id)
        void this.refresh(otherKey, otherEntry);
    }
    const entry = this.entries.get(key);
    if (!entry || entry.denied) return true;
    entry.version = Math.max(entry.version, signal.version);
    entry.checks = 0;
    entry.staleRetries = 0;
    this.notify(entry, false);
    clearTimeout(entry.timer);
    entry.request?.abort();
    if (this.visible) void this.refresh(key, entry);
    return true;
  }

  /** Call on focus, restored visibility, reconnect, or explicit refresh. */
  reconcile(job?: JobReference): void {
    if (!this.visible) return;
    for (const [key, entry] of this.entries) {
      if (job && key !== jobKey(job)) continue;
      if (entry.denied && !job) continue;
      entry.denied = false;
      entry.checks = 0;
      entry.staleRetries = 0;
      this.notify(entry, false);
      clearTimeout(entry.timer);
      void this.refresh(key, entry);
    }
  }

  setVisible(visible: boolean): void {
    if (this.visible === visible) return;
    this.visible = visible;
    if (visible) this.reconcile();
    else for (const entry of this.entries.values()) this.cancel(entry);
  }

  /** Identity and client-scope changes must dispose the previous coordinator. */
  dispose(): void {
    for (const entry of this.entries.values()) this.cancel(entry);
    this.entries.clear();
    this.versions.clear();
    this.signalVersions.clear();
  }

  private cancel(entry: Entry): void {
    clearTimeout(entry.timer);
    entry.request?.abort();
    entry.again = false;
  }

  private notify(entry: Entry, delayed: boolean): void {
    for (const observer of entry.observers) observer.delayed(delayed);
  }

  private schedule(key: string, entry: Entry): void {
    clearTimeout(entry.timer);
    if (!this.visible || entry.denied || !entry.unfinished ||
        !(entry.result?.requester ?? (this.signalVersions.has(key) || [...entry.observers].some(observer => observer.requester)))) return;
    if (entry.checks >= MAX_CHECKS) {
      this.notify(entry, true);
      return;
    }
    entry.timer = setTimeout(() => {
      entry.checks += 1;
      const [owner, kind] = JSON.parse(key) as string[];
      trace.getTracer("job-delivery").startSpan("job.freshness_check", {
        attributes: { "job.owner": owner, "job.kind": kind, "job.freshness_check": entry.checks },
      }).end();
      void this.refresh(key, entry);
    }, FRESHNESS_MS);
  }

  private async refresh(key: string, entry: Entry): Promise<void> {
    if (!this.visible || entry.denied || this.entries.get(key) !== entry) return;
    if (entry.request) {
      entry.again = true;
      return;
    }
    const observer = entry.observers.values().next().value;
    if (!observer) return;
    const request = new AbortController();
    entry.request = request;
    let stale = false;
    try {
      const result = await observer.read(request.signal, entry.version);
      if (request.signal.aborted || this.entries.get(key) !== entry) return;
      if (result.accessDenied) {
        entry.denied = true;
        entry.result = undefined;
        for (const view of entry.observers) view.received?.(result);
        this.cancel(entry);
        return;
      }
      const resultKey = result.job ? jobKey(result.job) : key;
      const minimumVersion = resultKey === key ? entry.version : this.versions.get(resultKey) ?? 0;
      if (result.version >= minimumVersion) {
        result.apply?.();
        entry.result = result;
        for (const view of entry.observers) view.received?.(result);
        entry.version = result.version;
        this.versions.set(resultKey, result.version);
        entry.unfinished = result.unfinished;
        entry.staleRetries = 0;
        if (!result.unfinished) this.notify(entry, false);
      } else {
        stale = true;
      }
    } catch (error) {
      // Preserve the last confirmed state; an unavailable read is not job failure.
      if (!request.signal.aborted)
        for (const view of entry.observers) view.failed?.(error);
    } finally {
      entry.request = undefined;
      if (this.entries.get(key) !== entry || !this.visible || entry.denied) return;
      if (entry.again) {
        entry.again = false;
        void this.refresh(key, entry);
      } else if (stale && entry.staleRetries < 3) {
        entry.staleRetries += 1;
        entry.timer = setTimeout(() => void this.refresh(key, entry), 1000 * entry.staleRetries);
      } else {
        this.schedule(key, entry);
      }
    }
  }
}
